<template>
  <div
    :class="[
      'bg-white border border-[#d2e0e3] flex flex-col size-full text-blue items-center justify-center overflow-hidden',
      'dark:bg-blue dark:border-blue dark:text-white',
      !popout && !standalone && 'rounded-xl',
    ]"
  >
    <div class="flex grow size-full items-center justify-center">
      <div class="relative flex flex-col size-full">
        <div class="flex flex-row pp:flex-col grow items-center pp:justify-center justify-items-stretch gap-4 pp:xs:gap-8 p-4">
          <player-button
            :href="episode.url"
            class="
              border rounded-lg
              pl:hidden pl:xs:block
              pp:hidden pp:phlg:block
            "
          >
            <img
              :src="(playlist ?? episode).image_url"
              class="
                min-w-24 min-h-24
                max-w-[calc(100vw-2rem)] xs:max-w-[30vw] sm:max-w-[40vw] pp:xs:max-w-[50vw]
                max-h-[calc(100dvh-24rem)] xs:max-h-[calc(100dvh-5.5rem)]
                aspect-square object-cover rounded-lg
              "
              :alt="playlist?.name ?? config('podcasts.iatl.name')"
            >
          </player-button>

          <div class="grid gap-1 sm:phmd:gap-2 md:phmd:gap-3 lg:phmd:gap-4 pp:gap-3 sm:text-lg lg:text-2xl xl:text-3xl 2xl:text-4xl">
            <div
              class="
                font-medium text-gray-500 dark:text-gray-400
                sm:text-md lg:text-xl xl:text-2xl 2xl:text-3xl
              "
            >
              <player-button href="https://www.lawhub.org">
                LawHub
              </player-button>

              presents

              <player-button :href="route('podcasts.iatl')">
                I Am The Law
              </player-button>

              <span v-if="playlist">:&nbsp;</span>

              <player-button v-if="playlist" :href="playlist.url">
                {{ playlist.name }}
              </player-button>
            </div>

            <h1 class="font-semibold text-xl leading-snug sm:text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl">
              <player-button class="line-clamp-2" :href="episode.url" :title="episode.title" variant="title">
                {{ episode.title }}
              </player-button>
            </h1>

            <p class="flex flex-wrap gap-1 font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
              Episode {{ episode.id }}

              <span aria-hidden="true">·</span>

              {{ episode.date }}
            </p>

            <p class="flex flex-wrap gap-2 md:gap-3 lg:gap-4 pp:gap-2 font-medium text-gray-500 dark:text-gray-400">
              Listen on

              <player-button
                :href="config('podcasts.iatl.apple')"
                aria-label="Apple Podcasts"
                title="Listen to I Am The Law on Apple Podcasts"
                class="group"
              >
                <icon name="apple-podcasts" class="fill-current"/>
                <span class="group-hover:underline">Apple Podcasts</span>
              </player-button>

              <span class="sr-only">and</span>

              <player-button
                :href="config('podcasts.iatl.spotify')"
                aria-label="Spotify"
                title="Listen to I Am The Law on Spotify"
                class="group"
              >
                <icon name="spotify" class="fill-current"/>
                <span class="group-hover:underline">Spotify</span>
              </player-button>

              <player-button
                :href="config('podcasts.iatl.lawhub')"
                aria-label="LawHub"
                title="Listen to I Am The Law on LawHub"
                class="group"
              >
                <icon name="lawhub" class="fill-current"/>
                <span class="group-hover:underline">LawHub</span>
              </player-button>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="relative bg-blue-100/75 border-t border-[#d2e0e3] dark:bg-gray-800/75 dark:border-blue w-full">
      <audio id="player" controls v-bind="{ autoplay }">
        <source :src="`${episode.media_url}?played_on=${source}`" type="audio/mp3">
      </audio>

      <div ref="controls" class="plyr__controls flex flex-row pp:flex-col justify-center gap-1 pp:!pb-2 shrink-0">
        <div class="flex items-center justify-center gap-1 pp:gap-1.5">
          <player-control
            title="Previous episode"
            class="pl:tooltip:ml-9 pl:tooltip:before:-ml-9"
            icon="backward"
            :href="previous ? `${params(previous, { autoplay, theme })}` : null"
            :disabled="!previous"
          />

          <button class="plyr__control pp:bg-blue pp:text-white dark:pp:bg-white dark:pp:text-blue pp:rounded-full" aria-label="Play" data-plyr="play" type="button">
            <svg class="icon--pressed pp:!size-10 pp:!p-1.5" role="presentation"><use xlink:href="#plyr-pause"/></svg>
            <svg class="icon--not-pressed pp:relative pp:left-0.5 pp:!size-10 pp:!p-1.5" role="presentation"><use xlink:href="#plyr-play"/></svg>
            <span class="label--pressed plyr__tooltip" role="tooltip">Pause</span>
            <span class="label--not-pressed plyr__tooltip" role="tooltip">Play</span>
          </button>

          <player-control
            title="Next episode"
            icon="forward"
            :href="next ? `${params(next, { autoplay, theme })}` : null"
            :disabled="!next"
          />
        </div>

        <div class="flex w-full gap-2 items-center pp:px-3">
          <div class="plyr__progress w-full pp:order-2">
            <input
              aria-label="Seek"
              data-plyr="seek"
              type="range"
              min="0"
              max="100"
              step="0.01"
              value="0"
            >
            <progress class="plyr__progress__buffer" min="0" max="100" value="0">
              % buffered
            </progress>
            <span role="tooltip" class="plyr__tooltip">00:00</span>
          </div>

          <div class="plyr__time plyr__time--current pp:order-1" aria-label="Current time">
            00:00
          </div>

          <div class="plyr__time plyr__time--duration !block before:!-ml-1 before:!mr-1 pp:before:hidden pp:order-3" aria-label="Duration">
            00:00
          </div>
        </div>

        <div class="flex gap-1 pp:gap-2 pp:xs:gap-6">
          <l-popover
            class="pp:absolute pp:top-4 pp:left-4 pp:m-0.5 pp:border pp:border-transparent"
            title="Volume"
          >
            <template #activator="{ open }">
              <player-control v-bind="{ open }">
                <svg v-if="muted" role="presentation"><use xlink:href="#plyr-muted"/></svg>
                <svg v-else role="presentation"><use xlink:href="#plyr-volume"/></svg>
              </player-control>
            </template>

            <focus-first class="px-2" selector="input">
              <input
                v-model.number="volume"
                aria-label="Volume"
                autocomplete="off"
                class="focus-visible:ring-0"
                min="0"
                max="1"
                step="0.05"
                type="range"
                :aria-valuenow="volume * 100"
                :aria-valuetext="`${volume * 100}%`"
                :style="`--value: ${volume * 100}%`"
              >
            </focus-first>
          </l-popover>

          <l-menu
            class="pp:absolute pp:top-4 pp:right-4 pp:m-0.5 pp:border pp:border-transparent"
            title="Speed"
          >
            <template #activator="{ open }">
              <player-control v-bind="{ open }">
                {{ speed }}x
              </player-control>
            </template>

            <l-menu-item label="0.5x" @click="speed = 0.5"/>
            <l-menu-item label="0.75x" @click="speed = 0.75"/>
            <l-menu-item label="Normal" @click="speed = 1"/>
            <l-menu-item label="1.25x" @click="speed = 1.25"/>
            <l-menu-item label="1.5x" @click="speed = 1.5"/>
            <l-menu-item label="2x" @click="speed = 2"/>
          </l-menu>

          <player-modal v-if="episode.description" title="Notes">
            <template #activator="{ activator }">
              <player-control
                title="Notes"
                icon="notes"
                @click="activator"
              />
            </template>

            <div class="grid gap-6 text-blue dark:text-white">
              <div class="prose dark:prose-invert prose-sm sm:prose-base text-inherit" v-html="episode.description"/>
              <slot name="hosts"/>
              <slot name="sponsors"/>
            </div>
          </player-modal>

          <player-modal v-if="episode.transcript" title="Transcript">
            <template #activator="{ activator }">
              <player-control
                title="Transcript"
                icon="transcript"
                @click="activator"
              />
            </template>

            <div class="block prose dark:prose-invert prose-sm sm:prose-base text-blue dark:text-white" v-html="episode.transcript"/>
          </player-modal>

          <player-modal title="Episodes">
            <template #activator="{ activator }">
              <player-control
                title="Episodes"
                icon="list"
                @click="activator"
              />
            </template>

            <iatl-episode-list :playing="episode" v-bind="{ playlist, theme }">
              <template #loading>
                <div class="flex grow items-center justify-center">
                  <icon name="loading" size="6xl"/>
                </div>
              </template>
            </iatl-episode-list>
          </player-modal>

          <player-modal title="Playlists">
            <template #activator="{ activator }">
              <player-control
                title="Playlists"
                icon="playlist"
                @click="activator"
              />
            </template>

            <iatl-playlist-list :playing="playlist" v-bind="{ theme }">
              <template #loading>
                <div class="flex grow items-center justify-center">
                  <icon name="loading" size="6xl"/>
                </div>
              </template>
            </iatl-playlist-list>
          </player-modal>

          <player-link
            v-if="!popout && !standalone"
            v-slot="{ activator }"
            force-popout
            sync-time
            v-bind="{ episode, playlist, theme }"
          >
            <player-control
              icon="pip"
              title="Popout"
              @click="activator"
            />
          </player-link>

          <player-modal title="Having problems with the player?">
            <template #activator="{ activator }">
              <player-control
                title="Help"
                icon="troubleshoot"
                @click="activator"
              />
            </template>

            <div class="flex flex-col grow size-full gap-6 items-center justify-center text-center">
              <div class="flex flex-col grow size-full gap-6 items-center justify-center text-center">
                <p>If you're having trouble accessing the podcast, we also publish to these platforms:</p>

                <ul class="flex flex-wrap list-none gap-2 sm:gap-4 justify-center pl-0 text-sm sm:text-base text-white">
                  <li>
                    <player-button
                      :href="config('podcasts.iatl.lawhub')"
                      aria-label="LawHub"
                      title="Listen to I Am The Law on LawHub"
                      variant="primary"
                    >
                      <icon name="lawhub" class="fill-current"/>
                      LawHub
                    </player-button>
                  </li>
                  <li>
                    <player-button
                      :href="config('podcasts.iatl.apple')"
                      aria-label="Apple Podcasts"
                      title="Listen to I Am The Law on Apple Podcasts"
                      variant="primary"
                    >
                      <icon name="apple-podcasts" class="fill-transparent dark:fill-white"/>
                      Apple Podcasts
                    </player-button>
                  </li>
                  <li>
                    <player-button
                      :href="config('podcasts.iatl.spotify')"
                      aria-label="Spotify"
                      title="Listen to I Am The Law on Spotify"
                      variant="primary"
                    >
                      <icon name="spotify" class="fill-current"/>
                      Spotify
                    </player-button>
                  </li>
                </ul>
              </div>

              <div class="flex flex-wrap items-center justify-center gap-4 text-sm whitespace-nowrap">
                <player-button :href="route('privacy-policy')" class="text-gray-500 dark:text-gray-400">
                  Privacy Policy
                </player-button>

                <player-button :href="route('terms')" class="text-gray-500 dark:text-gray-400">
                  Terms and Conditions
                </player-button>
              </div>
            </div>
          </player-modal>
        </div>
      </div>
    </div>

    <player-button
      v-if="popout"
      class="
        absolute top-2 right-2 flex items-center justify-center size-8 p-2
        rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300
      "
      icon="close"
      title="Close player"
      @click="close"
    />
  </div>
</template>

<script setup>
import FocusFirst from "@/components/FocusFirst.vue"
import IatlEpisodeList from "@/components/podcasts/IatlEpisodeList.vue"
import IatlPlaylistList from "@/components/podcasts/IatlPlaylistList.vue"
import Icon from "@/components/Icon.vue"
import LMenu from "@/components/menus/LMenu.vue"
import LMenuItem from "@/components/menus/LMenuItem.vue"
import LPopover from "@/components/popovers/LPopover.vue"
import PlayerButton from "@/components/player/PlayerButton.vue"
import PlayerControl from "@/components/player/PlayerControl.vue"
import PlayerLink from "@/components/player/PlayerLink.vue"
import PlayerModal from "@/components/player/PlayerModal.vue"
import Plyr from "plyr"
import route from "ziggy-js"
import { computed, onMounted, ref, watch } from "vue"
import { config, params, sleep } from "@/support"

const props = defineProps({
  autoplay: String,
  episode: Object,
  next: String,
  playlist: Object,
  previous: String,
  source: { type: String, default: window.opener ? "player" : "website" },
  theme: String,
  time: Number,
})

const controls = ref()

const popout = !!window.opener

const standalone = !!window.navigator.standalone

const $muted = ref(false)

const $speed = ref(1)

const $volume = ref(1)

const muted = computed({
  get: () => $muted.value,
  set: (value) => {
    $muted.value = value
    $volume.value = $volume.value === 0 ? 1 : $volume.value
  },
})

const speed = computed({
  get: () => $speed.value,
  set: (value) => $speed.value = value,
})

const volume = computed({
  get: () => ($muted.value ? 0 : $volume.value),
  set: (value) => {
    $muted.value = value === 0
    $volume.value = value
  },
})

const close = window.close

const seek = (time) => {
  if (!time) {
    return
  }

  window.plyr.currentTime = parseInt(time)

  if (window.plyr.currentTime) {
    return
  }

  sleep(100).then(() => seek(time))
}

onMounted(() => {
  const plyr = new Plyr("#player", { controls: controls.value })

  plyr.on("ready", () => {
    seek(props.time)

    $muted.value = window.plyr.muted
    $speed.value = window.plyr.speed
    $volume.value = window.plyr.volume
  })

  plyr.on("ratechange", () => {
    $speed.value = window.plyr.speed
  })

  plyr.on("volumechange", () => {
    $muted.value = window.plyr.muted
    $volume.value = window.plyr.volume
  })

  if (props.next) {
    plyr.on("ended", () => {
      window.location = params(props.next, { autoplay: true })
    })
  }

  window.plyr = plyr
})

watch($muted, value => window.plyr.muted = value)

watch($speed, value => window.plyr.speed = value)

watch($volume, value => window.plyr.volume = value)
</script>

<style>
  :root {
    --plyr-color-main: theme("colors.blue.DEFAULT");
    --plyr-audio-controls-background: transparent;
    --plyr-audio-control-color: theme("colors.blue.DEFAULT");
    --plyr-audio-control-color-hover: white;
  }

  .dark {
    --plyr-color-main: theme("colors.blue.500");
    --plyr-audio-control-color: white;
    --plyr-audio-control-color-hover: white;
  }

  .plyr__control:focus-visible {
    @apply outline-none;
  }

  .plyr--full-ui input[type="range"]:focus-visible::-webkit-slider-runnable-track {
    @apply outline-none;
  }

  .plyr--full-ui input[type="range"]:focus-visible::-moz-range-track {
    @apply outline-none;
  }

  .plyr--full-ui input[type="range"]:focus-visible::-ms-track {
    @apply outline-none;
  }
</style>
